import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ARCHETYPES, BADGES, CJ_AVATAR_URL } from "../lib/constants";
import { motion } from "framer-motion";
import { Sparkles, Flame, Trophy, Plus, BookOpen, ChevronRight, Crown, Camera, Loader2, Info } from "lucide-react";
import ColorCombo from "../components/ColorCombo";
import { playPointsSound } from "../lib/sound";
import { toast } from "sonner";

// Friendly rotating CJ greetings
const CJ_GREETINGS = [
  (n) => `Hey ${n}! Whose story are we banking today?`,
  (n) => `Welcome back, ${n}. The team's pipeline got hungrier overnight.`,
  (n) => `${n}! Got a fresh win to log? I'm all ears.`,
  (n) => `Look who's back. Let's add another proof point, ${n}.`,
  (n) => `${n}, you in? One story today and you're closer to the next badge.`,
  (n) => `Hey ${n}, your future-self on a discovery call will thank you for showing up.`,
];

const StatTile = ({ label, value, color, testid }) => (
  <div className="tc-card p-5" data-testid={testid}>
    <div className="tc-eyebrow text-slate-400">{label}</div>
    <div className="font-display text-5xl mt-1" style={{ color: color || "#083050" }}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recent, setRecent] = useState([]);
  const [myDrafts, setMyDrafts] = useState([]);
  const avatarInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/stats/me").catch(() => ({ data: null })),
      api.get("/stats/leaderboard").catch(() => ({ data: { leaderboard: [] } })),
      api.get("/stories", { params: { status: "approved" } }).catch(() => ({ data: [] })),
      api.get("/stories", { params: { status: "mine" } }).catch(() => ({ data: [] })),
    ]).then(([s, lb, all, mine]) => {
      setStats(s.data);
      setLeaderboard(Array.isArray(lb?.data?.leaderboard) ? lb.data.leaderboard : []);
      setRecent((Array.isArray(all?.data) ? all.data : []).slice(0, 6));
      setMyDrafts((Array.isArray(mine?.data) ? mine.data : []).filter((x) => x.status === "draft" || x.status === "rejected"));

      // "You earned new points since you were last here" celebration
      const seenKey = `tc_last_seen_points_${user.id}`;
      const lastSeen = parseInt(localStorage.getItem(seenKey) || "0", 10);
      const current = s?.data?.totals?.points || 0;
      if (current > lastSeen && lastSeen > 0) {
        const delta = current - lastSeen;
        playPointsSound();
        toast.success(`+${delta} points while you were away`, {
          description: "Nice work. Keep them coming.",
          duration: 4500,
        });
      }
      localStorage.setItem(seenKey, String(current));
    });
  }, [user]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large", { description: "Max 8 MB." });
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Profile photo updated!");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error("Couldn't update your photo", { description: detail });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  if (!user || !stats) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  const archetype = ARCHETYPES[user.archetype] || { color: "#f68b1f", label: "" };
  const totals = stats?.totals || { submitted: 0, points: 0 };
  const isFirstTimer = totals.submitted === 0;
  const firstName = (user.name || "there").split(" ")[0];
  // Pick a CJ greeting based on the day so it changes daily without re-shuffling every render
  const dailyGreeting = CJ_GREETINGS[new Date().getDate() % CJ_GREETINGS.length](firstName);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      {/* CJ greeting strip */}
      <motion.div
        initial={{ y: -8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        className="flex items-start gap-3 mb-5"
        data-testid="cj-greeting"
      >
        <motion.img
          src={CJ_AVATAR_URL}
          alt="CJ"
          initial={{ scale: 0.7, rotate: -8 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 160, damping: 12, delay: 0.1 }}
          className="h-14 w-14 rounded-full object-cover shadow-md ring-2 ring-white shrink-0"
          data-testid="cj-greeting-avatar"
        />
        <div className="relative bg-white border border-slate-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm max-w-md">
          <span className="absolute -left-1.5 top-3 w-3 h-3 bg-white border-l border-b border-slate-200 rotate-45" />
          <div className="text-[10px] tracking-[0.18em] font-bold text-orange-600 uppercase">CJ</div>
          <div className="text-sm sm:text-base text-tc-ink leading-snug mt-0.5" data-testid="cj-greeting-text">{dailyGreeting}</div>
        </div>
      </motion.div>
      {/* Hero */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring" }}
        className="tc-card relative overflow-hidden p-6 sm:p-8 tc-grain"
        data-testid="hero-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-center gap-4">
            <div className="relative group shrink-0">
              <div
                className="w-16 h-16 rounded-2xl bg-cover bg-center border-2"
                style={{ backgroundImage: `url(${avatarSrc(user)})`, borderColor: archetype.color }}
                data-testid="hero-avatar"
              />
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                data-testid="hero-avatar-upload"
                title="Change photo"
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center hover:scale-110 transition-transform"
              >
                {uploadingAvatar ? <Loader2 size={13} className="animate-spin text-slate-500" /> : <Camera size={13} className="text-slate-600" />}
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
                data-testid="hero-avatar-input"
              />
            </div>
            <div>
              <div className="tc-eyebrow text-slate-400">Welcome back</div>
              <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">{(user.name || "there").split(" ")[0]}.</h1>
              <ColorCombo user={user} className="mt-1.5" />
            </div>
          </div>
          <div className="sm:ml-auto flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => navigate("/capture")}
              data-testid="hero-new-story"
              className="h-12 px-5 rounded-2xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
              style={{ background: "#083050" }}
            >
              <Plus size={18} /> Capture a story
            </button>
            <Link to="/library" data-testid="hero-library-link" className="h-12 px-5 rounded-2xl border border-slate-200 font-bold flex items-center gap-2 hover:bg-slate-50">
              <BookOpen size={18} /> Browse library
            </Link>
          </div>
        </div>

        {isFirstTimer && (
          <div className="mt-6 p-4 rounded-2xl border border-orange-200 bg-orange-50 flex items-start gap-3" data-testid="first-story-callout">
            <Sparkles className="text-orange-600 shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-bold text-orange-900">Your first story is worth 100+ points.</div>
              <div className="text-sm text-orange-800/80 mt-0.5">Plus an "Energizer-grade" celebration moment. Don't skip it.</div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-6">
        <StatTile label="Points" value={totals.points} color="#f68b1f" testid="stat-points" />
        <StatTile label="Stories submitted" value={totals.submitted} color="#0091be" testid="stat-submitted" />
        <StatTile label="Approved" value={totals.approved} color="#7ac143" testid="stat-approved" />
        <div className="tc-card p-5" data-testid="stat-streak">
          <div className="tc-eyebrow text-slate-400 flex items-center gap-1"><Flame size={12} /> Current streak</div>
          <div className="font-display text-5xl mt-1" style={{ color: "#ffd400" }}>{totals.current_streak}</div>
          <div className="text-xs text-slate-500 mt-1">consecutive days w/ a story</div>
        </div>
      </div>

      {/* Drafts */}
      {myDrafts.length > 0 && (
        <section className="mt-8" data-testid="drafts-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Pick up where you left off</h2>
            <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">+30 bonus for returning</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {myDrafts.slice(0, 6).map((d) => (
              <Link to={`/capture/${d.id}`} key={d.id} className="tc-card p-5 hover:border-orange-300" data-testid={`draft-${d.id}`}>
                <div className="tc-eyebrow text-orange-600">{d.status === "rejected" ? "Send-back" : "Draft"}</div>
                <div className="font-display text-lg mt-1 line-clamp-2">{d.industry || "Untitled"}, {d.challenge_text || ","}</div>
                <div className="flex items-center mt-3 text-sm font-bold text-orange-600">Resume <ChevronRight size={16} /></div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Two-column: badges + leaderboard */}
      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <div className="tc-card p-6 lg:col-span-1" data-testid="badges-card">
          <h2 className="font-display text-xl flex items-center gap-2"><Trophy size={18} /> Badges</h2>
          <div className="flex flex-wrap gap-2 mt-3">
            {(stats.badges || []).length === 0 ? (
              <div className="text-sm text-slate-500">Unlock your first badge by submitting a story.</div>
            ) : (
              (stats.badges || []).map((b) => (
                <span key={b} className="tc-pill" style={{ background: BADGES[b]?.color || "#083050", color: "#fff" }} data-testid={`badge-${b}`}>
                  <Sparkles size={12} /> {BADGES[b]?.label || b}
                </span>
              ))
            )}
          </div>
        </div>

        <div className="tc-card p-6 lg:col-span-2" data-testid="leaderboard-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl flex items-center gap-2"><Crown size={18} /> Team leaderboard</h2>
            <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">All-time</span>
          </div>

          {/* How points work explainer */}
          <details className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 group" data-testid="points-explainer">
            <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between text-xs font-bold text-slate-700">
              <span className="flex items-center gap-1.5"><Info size={13} className="text-orange-500" /> How points work</span>
              <ChevronRight size={14} className="text-slate-400 transition-transform group-open:rotate-90" />
            </summary>
            <div className="px-3 pb-3 pt-1 grid sm:grid-cols-2 gap-1.5 text-[12px]" data-testid="points-explainer-rules">
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
                <span className="text-slate-700">Submit a complete story</span>
                <span className="font-display text-base text-orange-600">+100</span>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
                <span className="text-slate-700">Add measurable outcomes</span>
                <span className="font-display text-base" style={{ color: "#7ac143" }}>+50</span>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
                <span className="text-slate-700">Add a client quote</span>
                <span className="font-display text-base" style={{ color: "#0091be" }}>+25</span>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
                <span className="text-slate-700">Add a photo or video</span>
                <span className="font-display text-base" style={{ color: "#0091be" }}>+25</span>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 sm:col-span-2">
                <span className="text-slate-700">Come back and beef up an old draft</span>
                <span className="font-display text-base" style={{ color: "#ffd400" }}>+30</span>
              </div>
              <p className="sm:col-span-2 text-[11px] text-slate-500 leading-snug mt-1">
                Each bonus is awarded once per story. Add the missing pieces later and you'll still pick up the points.
              </p>
            </div>
          </details>
          <div className="mt-4 grid gap-2">
            {leaderboard.map((u, idx) => {
              const a = ARCHETYPES[u.archetype] || ARCHETYPES.blue;
              const isMe = u.id === user.id;
              const max = Math.max(1, ...leaderboard.map((x) => x.points || 0));
              const pct = Math.max(6, Math.round(((u.points || 0) / max) * 100));
              return (
                <div
                  key={u.id}
                  data-testid={`leaderboard-row-${idx + 1}`}
                  className={`flex items-center gap-3 rounded-2xl border p-3 ${isMe ? "border-orange-300 bg-orange-50/50" : "border-slate-200"}`}
                >
                  <div className="w-7 text-center font-display text-xl text-slate-400">{idx + 1}</div>
                  <div className="w-10 h-10 rounded-full bg-cover bg-center border-2 shrink-0" style={{ backgroundImage: `url(${avatarSrc(u)})`, borderColor: a.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-sm truncate">{u.name}{isMe && <span className="text-orange-500 ml-1">· you</span>}</div>
                      <ColorCombo user={u} variant="compact" />
                    </div>
                    <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: a.color }} />
                    </div>
                  </div>
                  <div className="font-display text-2xl" style={{ color: a.color }}>{u.points || 0}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent library */}
      <section className="mt-8" data-testid="recent-library">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">Fresh from the library</h2>
          <Link to="/library" className="text-sm font-bold text-orange-600 hover:underline">View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="tc-card p-8 text-center text-slate-500">No approved stories yet. Be the first.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recent.map((s) => (
              <Link to={`/story/${s.id}`} key={s.id} className="tc-card p-5" data-testid={`recent-${s.id}`}>
                <div className="tc-eyebrow text-slate-400">{s.industry}</div>
                <div className="font-display text-lg mt-1 line-clamp-2">{s.challenge_text}</div>
                <div className="text-sm text-slate-600 line-clamp-2 mt-1">{s.after_text}</div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
